import '@/global.css';

import { DefaultTheme, Stack, ThemeProvider } from 'expo-router';
import { useFonts } from 'expo-font';
import * as SplashScreen from 'expo-splash-screen';
import { useEffect } from 'react';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { PortalHost } from '@rn-primitives/portal';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { Uniwind } from 'uniwind';

import { AppStatusBar } from '@/components/AppStatusBar';
import { AppearanceProvider } from '@/lib/appearance';
import { AuthProvider } from '@/lib/auth';
import { BillingProvider } from '@/lib/billing';
import { appFonts } from '@/lib/fonts';
import { PowerSyncProvider } from '@/lib/powersync/provider';
import { Sentry } from '@/lib/sentry';

// "Field Manual" is a light-first design. Apply light synchronously to avoid a
// first-frame flash; AppearanceProvider then reconciles to the stored choice.
Uniwind.setTheme('light');

SplashScreen.preventAutoHideAsync();

function RootLayout() {
  const [fontsLoaded, fontError] = useFonts(appFonts);

  useEffect(() => {
    if (fontsLoaded || fontError) {
      SplashScreen.hideAsync();
    }
  }, [fontsLoaded, fontError]);

  if (!fontsLoaded && !fontError) {
    return null;
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <KeyboardProvider>
        <SafeAreaProvider>
          <AppearanceProvider>
            <AuthProvider>
              <PowerSyncProvider>
                <BillingProvider>
                  {/* BottomSheetModalProvider must nest INSIDE PowerSyncProvider (and
                      Auth/Appearance/Billing): @gorhom/portal teleports sheet content to
                      a host rendered at this provider's location, so sheet children only
                      see contexts provided ABOVE it. Kept below Gesture/Keyboard/SafeArea,
                      which gorhom needs. Without this, useQuery inside a sheet returns
                      empty ("PowerSync not configured"). */}
                  <BottomSheetModalProvider>
                    <ThemeProvider value={DefaultTheme}>
                      <Stack screenOptions={{ headerShown: false }}>
                        <Stack.Screen name="(app)" />
                        <Stack.Screen name="sign-in" options={{ title: 'Sign in' }} />
                        <Stack.Screen name="sign-up" options={{ title: 'Sign up' }} />
                        <Stack.Screen
                          name="paywall"
                          options={{
                            presentation: 'transparentModal',
                            animation: 'fade',
                          }}
                        />
                      </Stack>
                      <AppStatusBar />
                      <PortalHost />
                    </ThemeProvider>
                  </BottomSheetModalProvider>
                </BillingProvider>
              </PowerSyncProvider>
            </AuthProvider>
          </AppearanceProvider>
        </SafeAreaProvider>
      </KeyboardProvider>
    </GestureHandlerRootView>
  );
}

export default Sentry.wrap(RootLayout);
